import React, { useEffect, useState } from 'react';
import { signUp, confirmSignUp } from 'aws-amplify/auth';
import { Eye, EyeOff, Check, X, ArrowLeft } from 'lucide-react';
import { getSites, getContainers } from './dynamodb';

const API_ENDPOINT = process.env.REACT_APP_API_URL;

function StoreLogo() {
  return (
    <div className="flex items-center gap-3 justify-center mb-8">
      <img src="/Logo.png" alt="StoreHere" className="h-16 w-auto" />
      <div>
        <span className="text-2xl font-bold text-gray-800">store</span>
        <span className="text-2xl font-bold text-orange-500">here</span>
        <div className="text-sm text-gray-500 uppercase tracking-wider">SELF STORAGE</div>
      </div>
    </div>
  );
}

function PasswordRequirements({ password, showRequirements }) {
  const requirements = [
    { text: 'At least 8 characters', test: (pwd) => pwd.length >= 8 },
    { text: 'One uppercase letter', test: (pwd) => /[A-Z]/.test(pwd) },
    { text: 'One lowercase letter', test: (pwd) => /[a-z]/.test(pwd) },
    { text: 'One number', test: (pwd) => /\d/.test(pwd) },
    { text: 'One special character', test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd) }
  ];

  if (!showRequirements) return null;

  return (
    <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</div>
      <div className="space-y-1">
        {requirements.map((req, index) => {
          const isValid = req.test(password);
          return (
            <div key={index} className="flex items-center gap-2 text-sm">
              {isValid ? (
                <Check size={16} className="text-green-600" />
              ) : (
                <X size={16} className="text-gray-400" />
              )}
              <span className={isValid ? 'text-green-600' : 'text-gray-600'}>
                {req.text}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SignUp({ onSignIn }) {
  // Availability gating
  const [checkingAvailability, setCheckingAvailability] = useState(true);
  const [containersAvailable, setContainersAvailable] = useState(true);
  const [waitlistStatus, setWaitlistStatus] = useState('idle'); // idle | sending | success | error

  const [step, setStep] = useState('signup'); // 'signup' or 'confirm'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [codeDelivery, setCodeDelivery] = useState(null);
  const [loginUsername, setLoginUsername] = useState('');

  // Registration type
  const [registrationType, setRegistrationType] = useState('company'); // 'company' or 'personal'

  // Form data
  const [formData, setFormData] = useState({
    // Basic account info
    email: '',
    password: '',
    confirmPassword: '',
    
    // Personal details
    given_name: '',
    family_name: '',
    mobile_phone: '',
    title: '',
    
    // Company details (only for company registration)
    company_name: '',
    abn: '',
    
    // Verification method (email only)
    verification_method: 'EMAIL',
    
    // Contact details
    home_address: '',
    home_postcode: '',
    postal_address: '', // Optional
    postal_postcode: '', // Optional
    work_phone: '',
    
    // Preferences
    electronic_consent: false,
    
    // ACP (Authorised Contact Person) details
    acp_title: '',
    acp_first_name: '',
    acp_surname: '',
    acp_address: '',
    acp_postcode: '',
    acp_mobile_phone: '',
    acp_email: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // Check availability on load; if none, show waiting-list immediately
  useEffect(() => {
    const run = async () => {
      try {
        const sites = await getSites();
        let available = false;
        for (const site of sites) {
          const containers = await getContainers(site.id);
          const match = containers.find(container => {
            if (container.type !== 'container') return false;
            let status = container.status;
            if (container.subscriptionStatus === 'active' && container.securityBondStatus === 'paid') {
              status = 'rented-paid';
            } else if (container.subscriptionStatus === 'past_due' || container.overdueSince) {
              status = 'rented-unpaid';
            } else if (container.subscriptionStatus === 'canceled' || container.subscriptionStatus === 'inactive') {
              status = 'available';
            }
            return status === 'available';
          });
          if (match) { available = true; break; }
        }
        setContainersAvailable(!!available);
      } catch (e) {
        console.error('Availability check failed:', e);
        setContainersAvailable(false);
      } finally {
        setCheckingAvailability(false);
      }
    };
    run();
  }, []);

  const notifyWaitingListBackend = async (email) => {
    const res = await fetch(`${API_ENDPOINT}/waiting-list-notify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ email }).toString()
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  };

  const handleJoinWaitlist = async () => {
    if (!formData.email) {
      setError('Please enter your email to join the waiting list');
      return;
    }
    try {
      setWaitlistStatus('sending');
      await notifyWaitingListBackend(formData.email);
      setWaitlistStatus('success');
      setTimeout(() => setWaitlistStatus('idle'), 4000);
    } catch (e) {
      console.error('Waiting list notify error:', e);
      setWaitlistStatus('error');
      setTimeout(() => setWaitlistStatus('idle'), 4000);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return phone;
    // Remove all non-digit characters except the + sign
    return phone.replace(/[^\d+]/g, '');
  };

  const validateForm = () => {
    // Basic required fields for all registration types
    const basicRequiredFields = [
      'email', 'password', 'given_name', 'family_name', 'mobile_phone', 
      'title', 'home_address', 'home_postcode', 
      'acp_title', 'acp_first_name', 'acp_surname', 'acp_address', 
      'acp_postcode', 'acp_mobile_phone', 'acp_email'
    ];

    // Additional required fields for company registration
    const companyRequiredFields = ['company_name', 'abn', 'work_phone'];

    // Determine which fields to validate based on registration type
    const requiredFields = registrationType === 'company' 
      ? [...basicRequiredFields, ...companyRequiredFields]
      : basicRequiredFields;

    for (const field of requiredFields) {
      if (!formData[field]?.trim()) {
        const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        setError(`${fieldName} is required`);
        scrollToTop();
        return false;
      }
    }

    // Password validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      scrollToTop();
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      scrollToTop();
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      scrollToTop();
      return false;
    }

    if (!emailRegex.test(formData.acp_email)) {
      setError('Please enter a valid ACP email address');
      scrollToTop();
      return false;
    }

    // Validate that ACP details are different from personal details
    if (formData.acp_email === formData.email) {
      setError('Authorised Contact Person email must be different from your personal email');
      scrollToTop();
      return false;
    }

    if (formData.acp_mobile_phone === formData.mobile_phone) {
      setError('Authorised Contact Person mobile phone must be different from your personal mobile phone');
      scrollToTop();
      return false;
    }

    if (formData.acp_first_name === formData.given_name && formData.acp_surname === formData.family_name) {
      setError('Authorised Contact Person must be a different person than yourself');
      scrollToTop();
      return false;
    }

    // Phone number validation (basic)
    const phoneRegex = /^[\d\s\-\(\)\+]+$/;
    const phonesToValidate = [formData.mobile_phone, formData.acp_mobile_phone];
    
    // Add work phone validation only for company registration
    if (registrationType === 'company') {
      phonesToValidate.push(formData.work_phone);
    }

    for (const phone of phonesToValidate) {
      if (phone && !phoneRegex.test(phone)) {
        setError('Please enter valid phone numbers');
        scrollToTop();
        return false;
      }
    }

    // Electronic consent validation
    if (!formData.electronic_consent) {
      setError('You must consent to electronic correspondence');
      scrollToTop();
      return false;
    }

    return true;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Prepare user attributes for Cognito (standard + custom)
      const userAttributes = {
        // Standard
        email: formData.email,
        given_name: formData.given_name,
        family_name: formData.family_name,
        phone_number: formData.mobile_phone.startsWith('+')
          ? formatPhoneNumber(formData.mobile_phone)
          : `+61${formData.mobile_phone.replace(/^0/, '')}`,
        // Custom (must exist in the pool)
        'custom:title': formData.title,
        'custom:home_address': formData.home_address,
        'custom:home_postcode': formData.home_postcode,
        'custom:postal_address': formData.postal_address,
        'custom:postal_postcode': formData.postal_postcode,
        'custom:acp_title': formData.acp_title,
        'custom:acp_first_name': formData.acp_first_name,
        'custom:acp_surname': formData.acp_surname,
        'custom:acp_address': formData.acp_address,
        'custom:acp_postcode': formData.acp_postcode,
        'custom:acp_mobile_phone': formData.acp_mobile_phone,
        'custom:acp_email': formData.acp_email,
      };

      // Add company-specific attributes only for company registration
      if (registrationType === 'company') {
        userAttributes['custom:company_name'] = formData.company_name;
        userAttributes['custom:abn'] = formData.abn;
        userAttributes['custom:work_phone'] = formData.work_phone;
      }

      // Always verify via email (no phone option)
      const selectedUsername = formData.email;
      setLoginUsername(selectedUsername);
      try { sessionStorage.setItem('signup_username', selectedUsername); } catch {}

      // Debug: log exact phone and username we will send to Cognito
      try {
        console.log('SignUp debug: phone_number (E.164) ->', userAttributes.phone_number);
        console.log('SignUp debug: username used for signUp ->', selectedUsername);
      } catch {}

      const result = await signUp({
        username: selectedUsername,
        password: formData.password,
        options: {
            userAttributes: userAttributes,
            autoSignIn: {
              enabled: false
            }
        }
      });

      console.log('Sign up successful:', result);
      const delivery = result?.nextStep?.codeDeliveryDetails;
      try { console.log('SignUp debug: codeDeliveryDetails ->', delivery); } catch {}
      if (delivery) {
        setCodeDelivery(delivery);
      }
      setStep('confirm');
    } catch (error) {
      console.error('Sign up error:', error);
      setError(error.message || 'Sign up failed');
      scrollToTop();
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    
    if (!confirmationCode.trim()) {
      setError('Please enter the confirmation code');
      return;
    }

    setLoading(true);
    setError('');

    try {
        const usernameToUse = loginUsername || (() => { try { return sessionStorage.getItem('signup_username') || ''; } catch { return ''; } })() || formData.email;
        await confirmSignUp({
            username: usernameToUse,
            confirmationCode: confirmationCode
        });

        // Sign in the user after confirmation
        const { signIn } = await import('aws-amplify/auth');
        await signIn({ 
          username: usernameToUse, 
          password: formData.password 
        });

        // Redirect to signature page after successful confirmation
        window.location.href = '/signature';
    } catch (error) {
        console.error('Confirmation error:', error);
        setError(error.message || 'Confirmation failed');
    } finally {
        setLoading(false);
    }
  };

  const resendConfirmationCode = async () => {
    try {
      const { resendSignUpCode } = await import('aws-amplify/auth');
      const usernameToUse = loginUsername || (() => { try { return sessionStorage.getItem('signup_username') || ''; } catch { return ''; } })() || formData.email;
      await resendSignUpCode({ username: usernameToUse });
      setError('');
      alert('Confirmation code sent successfully!');
    } catch (error) {
      setError('Failed to resend confirmation code');
    }
  };

  if (step === 'confirm') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <StoreLogo />
            
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
              Confirm Your Account
            </h2>

            <p className="text-sm text-gray-600 mb-6 text-center">
              We've sent a verification code to your email <strong>{formData.email}</strong>. Please enter the code below to activate your account.
            </p>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleConfirmSignUp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmation Code
                </label>
                <input
                  type="text"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter 6-digit code"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-2 px-4 rounded-lg font-medium"
              >
                {loading ? 'Confirming...' : 'Confirm Account'}
              </button>
            </form>

            <div className="text-center mt-4">
              <button
                onClick={resendConfirmationCode}
                className="text-sm text-orange-600 hover:text-orange-700"
              >
                Resend confirmation code
              </button>
            </div>

            <div className="text-center mt-6">
              <button
                onClick={() => setStep('signup')}
                className="text-sm text-gray-600 hover:text-gray-700 flex items-center gap-1 mx-auto"
              >
                <ArrowLeft size={16} />
                Back to registration
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (checkingAvailability) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking availability...</p>
        </div>
      </div>
    );
  }

  if (!containersAvailable) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8">
          <StoreLogo />

          <div className="text-center space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-4">
                Sorry, We're Currently Full
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                All our storage containers are currently occupied.
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-left">
              <h3 className="text-lg font-semibold text-orange-800 mb-4">Join Our Waiting List</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  placeholder="Your email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <button
                  onClick={handleJoinWaitlist}
                  disabled={waitlistStatus === 'sending' || !formData.email}
                  className={`font-semibold py-3 px-6 rounded-lg transition-colors ${
                    waitlistStatus === 'sending' || !formData.email
                      ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                      : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
                >
                  {waitlistStatus === 'sending' ? 'Joining...' : 'Join Waiting List'}
                </button>
              </div>
              {waitlistStatus === 'success' && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                  Thanks! We've notified our team and will contact you soon.
                </div>
              )}
              {waitlistStatus === 'error' && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  Sorry, there was an error submitting your request. Please try again.
                </div>
              )}
            </div>

            <div className="text-gray-600">
              <p className="mb-2">Need immediate storage? We can help you find alternatives.</p>
              <p>
                Contact us at{' '}
                <a href="mailto:storage@storehere.com.au" className="text-orange-600 hover:underline">
                  storage@storehere.com.au
                </a>{' '}
                or call us at{' '}
                <a href="tel:0408805996" className="text-orange-600 hover:underline">
                  0408 805 996
                </a>
              </p>
            </div>

            <button
              onClick={() => window.location.href = '/'}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={16} /> Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <StoreLogo />
          
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
            Create Your StoreHere Account
          </h2>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSignUp} className="space-y-8">
            {/* Registration Type Selection */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Registration Type
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    registrationType === 'company' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => setRegistrationType('company')}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      id="company"
                      name="registrationType"
                      value="company"
                      checked={registrationType === 'company'}
                      onChange={() => setRegistrationType('company')}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                    />
                    <div>
                      <label htmlFor="company" className="font-medium text-gray-900 cursor-pointer">
                        Business Details
                      </label>
                      <p className="text-sm text-gray-600">
                        Register for your business with company details and ABN
                      </p>
                    </div>
                  </div>
                </div>

                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    registrationType === 'personal' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => setRegistrationType('personal')}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      id="personal"
                      name="registrationType"
                      value="personal"
                      checked={registrationType === 'personal'}
                      onChange={() => setRegistrationType('personal')}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                    />
                    <div>
                      <label htmlFor="personal" className="font-medium text-gray-900 cursor-pointer">
                        Personal Details
                      </label>
                      <p className="text-sm text-gray-600">
                        Register as an individual for personal storage needs
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Account Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Account Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      onFocus={() => setShowPasswordRequirements(true)}
                      onBlur={() => setShowPasswordRequirements(false)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <PasswordRequirements 
                    password={formData.password} 
                    showRequirements={showPasswordRequirements}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Personal Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Personal Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Select Title</option>
                    <option value="Mr">Mr</option>
                    <option value="Ms">Ms</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Miss">Miss</option>
                    <option value="Dr">Dr</option>
                    <option value="Prof">Prof</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.given_name}
                    onChange={(e) => handleInputChange('given_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.family_name}
                    onChange={(e) => handleInputChange('family_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Company Information - Only show for company registration */}
            {registrationType === 'company' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                  Company Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required={registrationType === 'company'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ABN <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.abn}
                      onChange={(e) => handleInputChange('abn', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="11 digits"
                      required={registrationType === 'company'}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Contact Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {registrationType === 'company' ? 'Company Address' : 'Home Address'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.home_address}
                    onChange={(e) => handleInputChange('home_address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {registrationType === 'company' ? 'Company Postcode' : 'Home Postcode'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.home_postcode}
                    onChange={(e) => handleInputChange('home_postcode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Address <span className="text-gray-500">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.postal_address}
                    onChange={(e) => handleInputChange('postal_address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="If different from home address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Postcode <span className="text-gray-500">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.postal_postcode}
                    onChange={(e) => handleInputChange('postal_postcode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div className={`grid grid-cols-1 gap-4 ${registrationType === 'company' ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.mobile_phone}
                    onChange={(e) => handleInputChange('mobile_phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="04XX XXX XXX"
                    required
                  />
                </div>

                {registrationType === 'company' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Work Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.work_phone}
                      onChange={(e) => handleInputChange('work_phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required={registrationType === 'company'}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Authorised Contact Person */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Alternate Contact Person (ACP)
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ACP Title <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.acp_title}
                    onChange={(e) => handleInputChange('acp_title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Select Title</option>
                    <option value="Mr">Mr</option>
                    <option value="Ms">Ms</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Miss">Miss</option>
                    <option value="Dr">Dr</option>
                    <option value="Prof">Prof</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ACP First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.acp_first_name}
                    onChange={(e) => handleInputChange('acp_first_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ACP Surname <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.acp_surname}
                    onChange={(e) => handleInputChange('acp_surname', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ACP Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.acp_address}
                    onChange={(e) => handleInputChange('acp_address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ACP Postcode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.acp_postcode}
                    onChange={(e) => handleInputChange('acp_postcode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ACP Mobile Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.acp_mobile_phone}
                    onChange={(e) => handleInputChange('acp_mobile_phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="04XX XXX XXX"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ACP Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.acp_email}
                    onChange={(e) => handleInputChange('acp_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Marketing Consent */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Preferences
              </h3>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="electronic_consent"
                  checked={formData.electronic_consent}
                  onChange={(e) => handleInputChange('electronic_consent', e.target.checked)}
                  className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor="electronic_consent" className="text-sm text-gray-700">
                  <span className="text-red-500">*</span> I consent to receiving correspondence (including Notices) from this Facility electronically (including email or SMS). It is your obligation to update your above details if they change.
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4 pt-6">
              <button
                type="button"
                onClick={() => window.location.href = '/signin'}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-3 px-6 rounded-lg font-medium"
              >
                Back to Sign In
              </button>
              
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-3 px-6 rounded-lg font-medium"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>

          <div className="text-center mt-6 text-sm text-gray-600">
            By creating an account, you agree to our Terms of Service and Privacy Policy
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUp;
